import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { sessionStorageHelper, Share, ShSharesDataService } from '@stylehaven/components';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { shareTokenResolver } from '../helpers/share-token-resolver.helper';

interface SharingResolverData {
    shareToken: string;
}

export const sharingResolver: ResolveFn<SharingResolverData> = async (route, state) => {
    const router: Router = inject(Router);
    const shareService: ShSharesDataService = inject(ShSharesDataService);

    let shareToken: string;
    const shareId: string = await getShareIdFromRoute(route, router);

    // Resolve again when changing share id
    if (shareId !== sessionStorageHelper.get('shareId')) {
        sessionStorageHelper.remove('shareToken');
        sessionStorageHelper.remove('shareId');
    }

    if (shareId === 'expired') {
        sessionStorageHelper.remove('shareToken');
        sessionStorageHelper.remove('shareId');
        return { shareToken: '' };
    }

    const share: Share | undefined = await shareService
        .getShareById(shareId)
        .pipe(catchError(() => of(undefined)))
        .toPromise();

    if (!share) {
        await router.navigateByUrl('/page-not-found', { skipLocationChange: true });
        sessionStorageHelper.remove('shareToken');
        sessionStorageHelper.remove('shareId');
        return { shareToken: '' };
    }

    // If shareId has changed, remove shareToken from session storage
    if (sessionStorageHelper.has('shareId') && sessionStorageHelper.get('shareId') !== shareId) {
        sessionStorageHelper.remove('shareToken');
    }

    sessionStorageHelper.set('shareId', shareId);

    if (sessionStorageHelper.has('shareToken')) {
        shareToken = sessionStorageHelper.get('shareToken')!;
    } else {
        shareToken = await shareTokenResolver(router, shareService, shareId).toPromise();
    }

    if (shareToken) {
        sessionStorageHelper.set('shareToken', shareToken);
    } else {
        console.error('shareToken not set ');
    }

    return { shareToken };
};

async function getShareIdFromRoute(route: ActivatedRouteSnapshot, router: Router): Promise<string> {
    const shareId: string | null = route.paramMap.get('shareId');

    if (!shareId) {
        sessionStorageHelper.remove('shareToken');
        sessionStorageHelper.remove('shareId');
        await router.navigateByUrl('/page-not-found', { skipLocationChange: true });
        return '';
    }

    return shareId;
}
